import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import styles from "../index.module.css";

function EligibilitySelfie() {
  const navigate = useNavigate();
  const userData = useEligibilityData();

  const [authToken, setAuthToken] = useState<{ mob: string; value: string }>({ mob: "", value: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const appId = userData?.applicationId;
    if (!appId) return;
    let cancelled = false;
    const token = sessionStorage.getItem("eligibility_auth_token");
    fetch(
      `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${appId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((res) => res.json())
      .then((res) => {
        if (cancelled) return;
        const stage: number = res?.data?.stage;
        if (stage === undefined || stage === 9) return;
        const navData = userData;
        if (stage === 11) { navigate("/eligibility/agreement?from=selfie", { replace: true, state: { data: navData } }); return; }
        if (stage === 12) { navigate("/eligibility/emandate", { replace: true, state: { data: navData } }); return; }
        if (stage === 8) { navigate("/eligibility/congratulations", { replace: true, state: { data: navData } }); return; }
        if (stage === 5) { navigate("/eligibility/under-review", { replace: true, state: { data: navData } }); return; }
        if (stage === 24) { navigate("/eligibility/digilocker", { replace: true, state: { data: navData } }); return; }
        if (stage === 25) { navigate("/eligibility/evaluating", { replace: true, state: { data: navData } }); return; }
        if (stage === 18) { navigate("/eligibility/rejected", { replace: true, state: { data: navData } }); return; }
        if (stage === 10) { navigate("/eligibility/waiting", { replace: true, state: { data: navData, fromStage10: true } }); return; }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userData?.applicationId]);

  useEffect(() => {
    if (!userData?.userId) return;
    let cancelled = false;
    const eligToken = sessionStorage.getItem("eligibility_auth_token");
    fetch(`${process.env.REACT_APP_DASHBOARD_URL}/integrations-login/generate-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(eligToken ? { Authorization: `Bearer ${eligToken}` } : {}),
      },
      body: JSON.stringify({
        mobile: userData?.mobile,
        userId: userData?.userId,
        applicationId: userData?.applicationId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setAuthToken({ mob: userData?.mobile, value: data.data });
        return fetch(`${process.env.REACT_APP_DASHBOARD_URL}/integrations-login/auth`, {
          headers: {
            Authorization: `Bearer ${data.data}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });
      })
      .then((res) => res?.json())
      .then((data) => {
        if (cancelled || !data) return;
        const selfieStatus = data.data?.selfieStatus;
        if (selfieStatus === 3) {
          navigate("/eligibility/agreement?from=selfie", { replace: true, state: { data: userData } });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userData?.userId, userData?.applicationId]);

  const handleSelfie = () => {
    if (!authToken.value) {
      setError("Please wait while we load the verification service.");
      return;
    }
    setLoading(true);
    setError("");
    fetch(`${process.env.REACT_APP_DASHBOARD_URL}/selfie/create`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken.value}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);
        if (res.message === "success") {
          const redirectUrl = `${process.env.REACT_APP_FRONTEND_URL}/eligibility/agreement?from=selfie`;
          window.open(
            `${process.env.REACT_APP_DIGIO_BASEURL}/#/gateway/login/${res.data.entityId}/vI3atY/${authToken.mob}?token_id=${res.data.tokenId}&redirect_url=${redirectUrl}`,
            "_self",
            "noopener,noreferrer"
          );
        } else {
          setError("Failed to start selfie verification. Please try again.");
        }
      })
      .catch(() => {
        setLoading(false);
        setError("Failed to start selfie verification. Please try again.");
      });
  };

  const features = [
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="#d32028" strokeWidth="1.7" />
          <path d="M12 7V12L15 14.5" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      label: "Quick",
      desc: "Under 30 secs",
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z"
            stroke="#d32028"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M9 12L11 14L15 10" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      label: "Secure",
      desc: "256-bit encrypted",
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="3.5" stroke="#d32028" strokeWidth="1.7" />
          <path d="M6 19C6 16.24 8.69 14 12 14C15.31 14 18 16.24 18 19" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M15 11.5L16.5 13L19.5 10" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      label: "Liveness",
      desc: "Face Match",
    },
  ];

  const checkItems = [
    "Good, even lighting on your face",
    "Face clearly visible — no glasses or mask",
    "Hold your phone steady",
  ];

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              {/* ── Header ── */}
              <div className={styles.cardHeader}>
                <div
                  style={{
                    width: "2.4rem",
                    height: "2.4rem",
                    borderRadius: "50%",
                    background: "#F4E5DD",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z"
                      fill="#d32028"
                      opacity="0.15"
                      stroke="#d32028"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="13" r="4" stroke="#d32028" strokeWidth="1.5" />
                  </svg>
                </div>
                <p className={styles.cardTitle}>Selfie Verification</p>
              </div>

              {/* ── Hero illustration ── */}
              <div style={{ display: "flex", justifyContent: "center", padding: "0.3rem 0 0.2rem" }}>
                <svg width="120" height="104" viewBox="0 0 200 155" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="100" cy="82" rx="72" ry="58" fill="#fff5f5" />
                  <rect x="62" y="22" width="76" height="112" rx="12" fill="white" stroke="#f9d8d6" strokeWidth="1.5" />
                  <rect x="62" y="22" width="76" height="112" rx="12" fill="#fde8e8" opacity="0.3" />
                  <rect x="86" y="27" width="28" height="7" rx="3.5" fill="#f9d8d6" />
                  <circle cx="100" cy="30.5" r="2.5" fill="#d32028" opacity="0.3" />
                  <ellipse cx="100" cy="82" rx="24" ry="28" fill="#fde8e8" />
                  <circle cx="93" cy="76" r="3" fill="#d32028" opacity="0.5" />
                  <circle cx="107" cy="76" r="3" fill="#d32028" opacity="0.5" />
                  <path
                    d="M92 88C92 88 95.5 92 100 92C104.5 92 108 88 108 88"
                    stroke="#d32028"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    opacity="0.5"
                  />
                  <path d="M70 52L70 44L78 44" stroke="#d32028" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M130 52L130 44L122 44" stroke="#d32028" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M70 112L70 120L78 120" stroke="#d32028" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M130 112L130 120L122 120" stroke="#d32028" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <g transform="translate(118, 90)">
                    <circle cx="14" cy="14" r="14" fill="#d32028" />
                    <circle cx="14" cy="14" r="14" fill="white" opacity="0.12" />
                    <path
                      d="M8 14L12 18L20 10"
                      stroke="white"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <circle cx="52" cy="48" r="4" fill="#fde8e8" />
                  <circle cx="155" cy="40" r="3" fill="#f9d8d6" />
                  <circle cx="46" cy="118" r="3" fill="#fde8e8" />
                  <circle cx="160" cy="108" r="2.5" fill="#f9d8d6" />
                </svg>
              </div>

              {/* ── Description ── */}
              <p
                style={{
                  textAlign: "center",
                  fontSize: "0.86rem",
                  color: "#666",
                  margin: "0 0 0.65rem",
                  lineHeight: 1.55,
                }}
              >
                Complete a quick selfie to verify your identity. Our AI-powered liveness check ensures your security.
              </p>

              {/* ── Feature tiles ── */}
              <div style={{ display: "flex", gap: "0.55rem", marginBottom: "0.65rem" }}>
                {features.map((item) => (
                  <div
                    key={item.label}
                    style={{
                      flex: 1,
                      background: "#fff9f9",
                      border: "1px solid #f9d8d6",
                      borderRadius: "10px",
                      padding: "0.55rem 0.4rem",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "0.2rem" }}>
                      {item.icon}
                    </div>
                    <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#222", marginBottom: "0.12rem" }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: "0.56rem", color: "#999" }}>{item.desc}</div>
                  </div>
                ))}
              </div>

              {/* ── Keep ready checklist ── */}
              <div
                style={{
                  background: "#fafafa",
                  border: "1px solid #efefef",
                  borderRadius: "10px",
                  padding: "0.65rem 1rem",
                }}
              >
                <p style={{ fontSize: "0.82rem", fontWeight: 700, color: "#333", margin: "0 0 0.35rem" }}>
                  Keep ready:
                </p>
                {checkItems.map((item) => (
                  <div
                    key={item}
                    style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.25rem" }}
                  >
                    <div
                      style={{
                        width: "13px",
                        height: "13px",
                        borderRadius: "50%",
                        background: "#d32028",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <svg width="8" height="7" viewBox="0 0 9 7" fill="none">
                        <path
                          d="M1.5 3.5L3.5 5.5L7.5 1.5"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span style={{ fontSize: "0.66rem", color: "#555" }}>{item}</span>
                  </div>
                ))}
              </div>

              {/* ── Powered by Digio ── */}
              <p
                style={{
                  fontSize: "0.68rem",
                  color: "#bbb",
                  textAlign: "center",
                  marginTop: "0.5rem",
                  marginBottom: 0,
                }}
              >
                Powered by{" "}
                <img
                  src="https://www.digio.in/images/digio_blue.png"
                  alt="Digio"
                  style={{ height: "0.85rem", verticalAlign: "middle", marginLeft: "0.2rem" }}
                />
              </p>

              {error && (
                <p style={{ color: "#d32028", fontSize: "0.85rem", marginTop: "0.75rem", marginBottom: 0 }}>
                  {error}
                </p>
              )}
            </div>

            {/* ── Proceed + Skip ── */}
            <div
              style={{
                padding: "0rem 1.25rem 1rem",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "0.6rem",justifyContent:'center'
              }}
            >
              <button
                onClick={handleSelfie}
                disabled={loading}
                style={{
                  width: "65%",
                  padding: "0.8rem 1.5rem",
                  background: loading ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                {loading ? "Loading..." : (
                  <>
                    Proceed to Selfie
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/agreement", { replace: true, state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilitySelfie;
